/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ClipboardEvent, KeyboardEvent, ReactElement, useRef, useState } from "react"

const INPUT_CLASS_NAME = [
  "w-12 h-14 text-center text-2xl font-semibold text-gray-800",
  "border border-gray-300 rounded-md",
  "focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100",
].join(" ")

/**
 * A 6-box one-time-code input (05 §3 Verify): digit-only, auto-advance on entry,
 * backspace steps back, and a paste splits across the boxes. Uncontrolled by
 * design — the parent receives the code via `onChange`/`onComplete` and clears
 * the boxes by remounting with a new `key`.
 */
const CodeInput = ({
  length = 6,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = true,
}: {
  length?: number
  onChange?: (code: string) => void
  onComplete?: (code: string) => void
  disabled?: boolean
  autoFocus?: boolean
}): ReactElement => {
  const [chars, setChars] = useState<string[]>(() => Array.from({ length }, () => ""))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  const focusBox = (index: number): void => {
    inputsRef.current[Math.max(0, Math.min(index, length - 1))]?.focus()
  }

  const update = (nextChars: string[]): void => {
    setChars(nextChars)
    const code = nextChars.join("")
    onChange?.(code)
    if (nextChars.every((char) => char !== "")) {
      onComplete?.(code)
    }
  }

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>): void => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1)
    if (!digit) {
      return
    }
    const next = [...chars]
    next[index] = digit
    update(next)
    focusBox(index + 1)
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== "Backspace") {
      return
    }
    const next = [...chars]
    if (chars[index]) {
      next[index] = ""
      update(next)
    } else if (index > 0) {
      next[index - 1] = ""
      update(next)
      focusBox(index - 1)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length).split("")
    if (digits.length === 0) {
      return
    }
    update(Array.from({ length }, (_, index) => digits[index] ?? ""))
    focusBox(digits.length)
  }

  return (
    <div className="flex gap-2 justify-center">
      {chars.map((char, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={char}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          aria-label={`Digit ${index + 1}`}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className={INPUT_CLASS_NAME}
        />
      ))}
    </div>
  )
}

export default CodeInput
